import React, { useEffect, useState, useMemo } from "react";
import axios from 'helpers/api';
// import {
//   PieChart,
//   Pie,
//   Cell,
//   ResponsiveContainer,
//   Tooltip,
//   Legend
// } from "recharts";
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  Legend as ReLegend
} from "recharts";
import { Card, Spinner, Alert, Button, Col, Row, Form } from "react-bootstrap";
import ThemedSelect from "components/commonbreadcrumb/ThemedSelect";
import DatePicker from 'react-datepicker';
import moment from "moment/moment";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableFooter from "components/common/advance-table/AdvanceTableFooter";
import useAdvanceTable from "hooks/useAdvanceTable";

const STATUS_COLORS = {
  "On time": "#4CAF50",      // green
  "Late": "#FFB020",         // amber
  "Early": "#5AC8FA",        // light blue
  "Missed": "#3B4A6B",       // navy
  "Refused": "#E43E5C",      // muted red
  "LOA/Hospital": "#8B5CF6"  // violet
};

const MedpassDashboard = () => {
  const [rawData, setRawData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [patientList, setPatientList] = useState([]);
  const [selectedPateint, setSelectedPatient] = useState(null);
  const [givenByList, setGivenByList] = useState([]);
  const [selectedGivenBy, setSelectedGivenBy] = useState(null);
  const [hovered, setHovered] = useState(null);
  // const [lineChartData, setLineChartData] = useState([]);
  const [timeDiffData, setTimeDiffData] = useState([]);
  const facilityList = JSON.parse(localStorage.getItem("facilitiesData"));
  let facilityObj = facilityList.reduce((obj, item) => {
    obj[item.value] = item.key;
    return obj;
  }, {})
  const today = moment();
  const oneMonthAgo = moment().subtract(1, "day");
  const [options] = useState([
    { value: '', key: 'All' },
    ...facilityList
  ]);
  const [filteredFacilities, setFilteredFacilities] = useState([]);
  const [filters, setFilters] = useState({
    facility: "",
    given_by: "",
    patient: "",
    startDate: oneMonthAgo.toDate(),
    endDate: today.toDate()
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (!rawData.length) return;

    let filtered = [...rawData];

    if (filters.facility)
      filtered = filtered.filter(
        (r) => String(r.facility_id) === String(filters.facility)
      );

    if (filters.patient)
      filtered = filtered.filter(
        (r) => String(r.patient_id) === String(filters.patient)
      );

    if (filters.given_by)
      filtered = filtered.filter(
        (r) =>
          (r.dose_given_by_username || "").toLowerCase() ===
          filters.given_by.toLowerCase()
      );

    setChartData(formatDoseData(filtered));
    // setLineChartData(getLineChartData(filtered));
    setTimeDiffData(getTimeDiffChartData(filtered));
  }, [filters, rawData]);

  useEffect(() => {
    if (!rawData.length) return;

    if (filters.facility) {
      const filteredRecords = rawData.filter(
        (r) => String(r.facility_id) === String(filters.facility)
      );

      const patients = [
        { key: "All", value: "" },
        ...filteredRecords.reduce((acc, item) => {
          const fullName = `${item.patient_lastname || ""} ${item.patient_firstname || ""}`.trim();
          if (item.patient_id && !acc.find((p) => p.value === item.patient_id)) {
            acc.push({ key: fullName, value: item.patient_id });
          }
          return acc;
        }, [])
      ];

      const givenBy = [
        { key: "All", value: "" },
        ...filteredRecords.reduce((acc, item) => {
          const username = item.dose_given_by_username?.trim();
          if (username && !acc.find((g) => g.value === username)) {
            acc.push({ key: username, value: username });
          }
          return acc;
        }, [])
      ];

      setPatientList(patients);
      setGivenByList(givenBy);
    } else {
      const patients = [
        { key: "All", value: "" },
        ...rawData.reduce((acc, item) => {
          const fullName = `${item.patient_lastname || ""} ${item.patient_firstname || ""}`.trim();
          if (item.patient_id && !acc.find((p) => p.value === item.patient_id)) {
            acc.push({ key: fullName, value: item.patient_id });
          }
          return acc;
        }, [])
      ];

      const givenBy = [
        { key: "All", value: "" },
        ...rawData.reduce((acc, item) => {
          const username = item.dose_given_by_username?.trim();
          if (username && !acc.find((g) => g.value === username)) {
            acc.push({ key: username, value: username });
          }
          return acc;
        }, [])
      ];

      setPatientList(patients);
      setGivenByList(givenBy);
    }
    setSelectedPatient(null);
    setSelectedGivenBy(null);
    setFilters((prev) => ({ ...prev, patient: "", given_by: "" }));
  }, [filters.facility]);


  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("accessToken");

      const payload = {
        from_date: moment(filters.startDate).format("MM/DD/YY"),
        to_date: moment(filters.endDate).format("MM/DD/YY")
      };

      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/medpass/medpass-dashboard`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const records = response.data.data || [];

      const facilityIdsInResponse = [
        ...new Set(records.map((r) => r.facility_id).filter(Boolean))
      ];

      const filteredFacilities = facilityList.filter((f) =>
        facilityIdsInResponse.includes(Number(f.value))
      );

      const facilities = [{ key: "All", value: "" }, ...filteredFacilities];

      const patients = [
        { key: "All", value: "" },
        ...records.reduce((acc, item) => {
          const fullName = `${item.patient_lastname || ""} ${item.patient_firstname || ""}`.trim();
          if (!acc.find((p) => p.value === item.patient_id)) {
            acc.push({ key: fullName, value: item.patient_id });
          }
          return acc;
        }, [])
      ];

      const givenBy = [
        { key: "All", value: "" },
        ...records.reduce((acc, item) => {
          const username = item.dose_given_by_username?.trim();
          if (username && !acc.find((g) => g.value === username)) {
            acc.push({ key: username, value: username });
          }
          return acc;
        }, [])
      ];
      setGivenByList(givenBy);
      setPatientList(patients);
      setFilteredFacilities(facilities);
      setRawData(records);
      setChartData(formatDoseData(records));
    } catch (error) {
      console.error("Error fetching Medpass data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Aggregate and format for pie chart
  const formatDoseData = (records) => {
    const totals = {
      "On time": 0,
      Late: 0,
      // Early: 0,
      Missed: 0,
      Refused: 0,
      "LOA/Hospital": 0
    };

    records.forEach((dose) => {
      if (dose.is_missed) totals.Missed += 1;
      if (dose.is_refused) totals.Refused += 1;
      if (dose.is_loa) totals["LOA/Hospital"] += 1;
      if (dose.is_late) totals.Late += 1;
      if (dose.on_time === 1) totals["On time"] += 1;
    });

    return Object.keys(totals).map((key) => ({
      name: key,
      value: totals[key]
    }));
  };

  const totalDoses = useMemo(
    () => chartData.reduce((sum, item) => sum + item.value, 0),
    [chartData]
  );

  const mainLabel = useMemo(() => {
    if (!chartData.length) return "";
    const max = chartData.reduce(
      (a, b) => (a.value > b.value ? a : b),
      chartData[0]
    );
    return max.name;
  }, [chartData]);

  const handleFacilityChange = (e) => {
    setSelectedFacility(e);
    setFilters((prev) => ({ ...prev, facility: e.value }));
  };

  const handlePatientChange = (e) => {
    setSelectedPatient(e);
    setFilters((prev) => ({ ...prev, patient: e.value }));
  };

  const handleGivenByChange = (e) => {
    setSelectedGivenBy(e);
    setFilters((prev) => ({ ...prev, given_by: e.value }));
  };

  // const getLineChartData = (records) => {
  //   const grouped = {};

  //   records.forEach((r) => {
  //     const date = moment(r.dose_date, ["MM/DD/YYYY", "YYYY-MM-DD"]).format("MMM DD");
  //     if (!grouped[date]) {
  //       grouped[date] = {
  //         date,
  //         "On time": 0,
  //         Late: 0,
  //         Missed: 0,
  //         Refused: 0,
  //         "LOA/Hospital": 0
  //       };
  //     }

  //     if (r.is_missed) grouped[date].Missed += 1;
  //     if (r.is_refused) grouped[date].Refused += 1;
  //     if (r.is_loa) grouped[date]["LOA/Hospital"] += 1;
  //     if (r.is_late) grouped[date].Late += 1;
  //     if (r.on_time === 1) grouped[date]["On time"] += 1;
  //   });
  //   return Object.values(grouped).sort((a, b) =>
  //     moment(a.date, "MMM DD").diff(moment(b.date, "MMM DD"))
  //   );
  // };

  const getTimeDiffChartData = (records) => {
    const grouped = {};
    const grouped2 = {};
    var result2 = [];

    records.forEach((r) => {
      if (r.is_dose_given !== 1) return; // only consider given doses

      if (r.time_diff === null || r.time_diff === undefined || r.time_diff === '') return; // skip if no time_diff

      // console.log(r, typeof r.time_diff);
      if (!grouped2[r.time_diff]) {
        grouped2[r.time_diff] = { time_diff: r.time_diff, records: [] };
      }
      grouped2[r.time_diff].records.push(r);
    });

    result2 = Object.values(grouped2).map((g, i) => ({ date: i, "Time Diff": g.time_diff, Count: g.records.length }));

    // sort result2 by result2.time_diff ascending
    result2 = result2.sort((a, b) => a["Time Diff"] - b["Time Diff"]);

    return result2;


    // return result.sort((a, b) =>
    //   moment(a.date, "MMM DD").diff(moment(b.date, "MMM DD"))
    // );
  };

  const columns = [
    {
      accessorKey: 'facility_name',
      header: 'Facility Name',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      },
    },
    {
      accessorKey: 'patient_count',
      header: 'Patients',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      },
    },

    // {
    //   accessorKey: 'patient',
    //   header: 'Patient',
    //   meta: {
    //     headerProps: { className: 'text-900 text-start px-2' },
    //     cellProps: { className: 'text-start px-2' }
    //   }
    // },
    {
      accessorKey: 'total',
      header: 'Total Medpass',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      },
    },
    {
      accessorKey: 'on_time',
      header: 'On Time',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      },
    },
    {
      accessorKey: 'late',
      header: 'Late',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      },
    },
    {
      accessorKey: 'missed',
      header: 'Missed',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      },
    },
    {
      accessorKey: 'refused',
      header: 'Refused',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      },
    },
    {
      accessorKey: 'loa',
      header: 'LOA / Hospital',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      },
    },
    {
      accessorKey: 'pending',
      header: 'Pending',
      meta: {
        headerProps: { className: 'text-900 text-start' },
        cellProps: { className: 'text-start' }
      },
    }
  ];

  const getTableData = useMemo(() => {
    const grouped = rawData.reduce((acc, item) => {
      const fName = facilityObj[item.facility_id] || 'Unknown Facility';
      const fId = item.facility_id;

      if (!acc[fId]) {
        acc[fId] = {
          facility_id: fId,
          facility_name: fName,
          patientIds: new Set(),
          patient_count: 0,
          total: 0,
          on_time: 0,
          late: 0,
          missed: 0,
          refused: 0,
          loa: 0,
          pending: 0
        };
      }

      acc[fId].total += 1;
      if (item.patient_id) acc[fId].patientIds.add(item.patient_id);

      if (item.on_time === 1) acc[fId].on_time += 1;
      else if (item.is_late) acc[fId].late += 1;
      else if (item.is_missed) acc[fId].missed += 1;
      else if (item.is_refused) acc[fId].refused += 1;
      else if (item.is_loa) acc[fId].loa += 1;
      else acc[fId].pending += 1;

      return acc;
    }, {});

    const result = Object.values(grouped).map((item) => ({
      ...item,
      patient_count: item.patientIds.size
    }));

    if (filters.facility) {
      return result.filter(
        (r) =>
          String(r.facility_id) === String(filters.facility)
      );
    }

    return result;
  }, [rawData, filters.facility]);


  const table = useAdvanceTable({
    data: getTableData,
    columns,
    selection: false,
    sortable: true,
    pagination: true
  });

  return (
    <Card className="p-4 shadow-sm">
      <h4 className="mb-4">Medpass Dashboard</h4>
      <div className="border rounded p-3 pb-4 mb-4">
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Start Date:</Form.Label>
              <DatePicker
                placeholderText="mm/dd/yy"
                className="form-control"
                selected={filters.startDate ? moment(filters.startDate).format("MM/DD/YY") : null}
                onChange={(date) =>
                  setFilters((prev) => ({ ...prev, startDate: date }))
                }
                dateFormat="MM/dd/yy"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>End Date:</Form.Label>
              <DatePicker
                placeholderText="mm/dd/yy"
                className="form-control"
                selected={filters.endDate ? moment(filters.endDate).format("MM/DD/YY") : null}
                onChange={(date) =>
                  setFilters((prev) => ({ ...prev, endDate: date }))
                }
                dateFormat="MM/dd/yy"
              />
            </Form.Group>
          </Col>
          <Col md={4} className="d-flex align-items-end">
            <Button variant="primary" onClick={fetchDashboardData} className="me-2">
              Search
            </Button>
          </Col>
        </Row>
      </div>
      <div className="filters-section border rounded pt-4 p-3 position-relative">
        <div className="filter-heading">Filters</div>

        <Row className="mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Facility</Form.Label>
              <ThemedSelect
                name="facility"
                closeMenuOnSelect
                placeholder="Select Facility"
                classNamePrefix="react-select"
                options={filteredFacilities}
                getOptionLabel={option => option.key}
                getOptionValue={option => option.value}
                onChange={handleFacilityChange}
                value={selectedFacility}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Patient</Form.Label>
              <ThemedSelect
                name="patient"
                closeMenuOnSelect
                placeholder="Select patient"
                classNamePrefix="react-select"
                options={patientList}
                value={selectedPateint}
                getOptionLabel={option => option.key}
                getOptionValue={option => option.value}
                onChange={handlePatientChange}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderColor: state.isFocused ? "#80bdff" : "#ced4da",
                    boxShadow: "none",
                    '&:hover': { borderColor: "#80bdff" },
                    minHeight: "38px"
                  }),
                  option: (base, { isFocused, isSelected }) => ({
                    ...base,
                    backgroundColor: isSelected
                      ? "#e9ecef"
                      : isFocused
                        ? "#f8f9fa"
                        : "#fff",
                    color: "#212529",
                    cursor: "pointer"
                  }),
                  singleValue: base => ({
                    ...base,
                    color: "#212529"
                  }),
                  placeholder: base => ({
                    ...base,
                    color: "#6c757d"
                  }),
                  menu: base => ({
                    ...base,
                    zIndex: 9999
                  })
                }}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Given by</Form.Label>
              <ThemedSelect
                placeholder="Select Given By"
                options={givenByList}
                getOptionLabel={(option) => option.key}
                getOptionValue={(option) => option.value}
                onChange={handleGivenByChange}
                value={selectedGivenBy}
              />
            </Form.Group>
          </Col>
        </Row>
      </div>
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : totalDoses === 0 ? (
        <Alert variant="info" className="text-center">
          No Medpass data found for this date range.
        </Alert>
      ) : (
        <>
          <Row className="mt-4">
            <Col md={6}>
              <div style={{ width: "100%", height: 380 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={90}
                      outerRadius={120}
                      paddingAngle={3}
                      dataKey="value"
                      onMouseEnter={(_, index) => setHovered(chartData[index])}
                      onMouseLeave={() => setHovered(null)}
                    >
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={STATUS_COLORS[entry.name]}
                          stroke="#fff"
                          strokeWidth={1}
                          style={{
                            cursor: "pointer",
                            transition: "transform 0.2s ease"
                          }}
                        />
                      ))}
                    </Pie>

                    <Tooltip
                      contentStyle={{
                        backgroundColor: "rgba(255,255,255,0.9)",
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                      }}
                      formatter={(value, name) => [`${value}`, `${name}`]}
                    />

                    <Legend
                      verticalAlign="middle"
                      align="left"
                      layout="vertical"
                      iconType="circle"
                      formatter={(value) => {
                        const item = chartData.find((d) => d.name === value);
                        return `${value} (${item?.value || 0})`;
                      }}
                      wrapperStyle={{
                        fontSize: "13px",
                        color: "#374151",
                        lineHeight: "24px"
                      }}
                    />
                    <text
                      x="60%"
                      y="46%"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      style={{
                        fontSize: "17px",
                        fontWeight: 600,
                        fill: "#111827"
                      }}
                    >
                      {hovered ? hovered.name : "Total"}
                    </text>
                    <text
                      x="60%"
                      y="54%"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      style={{
                        fontSize: "15px",
                        fill: "#6B7280"
                      }}
                    >
                      {hovered ? hovered.value : totalDoses}
                    </text>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Col>
            <Col md={6}>
              <div style={{ width: "100%", height: 380 }}>
                <ResponsiveContainer>
                  <LineChart
                    data={timeDiffData}
                    margin={{ top: 10, right: 24, left: 8, bottom: 24 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />

                    <XAxis
                      dataKey="Time Diff"
                      type="number"
                      stroke="#6B7280"
                      tickMargin={8}
                      domain={['auto', 'auto']}
                      label={{
                        value: 'Administration Time Deviation (min)',
                        position: 'insideBottom',
                        offset: -10,        // tighter to the axis
                        fill: '#6B7280',
                        fontSize: 12,
                        textAnchor: 'middle'
                      }}
                    />

                    <YAxis
                      stroke="#6B7280"
                      tickMargin={8}
                      label={{
                        value: 'Dose Frequency',
                        angle: -90,
                        position: 'insideLeft',
                        offset: 10,      
                        fill: '#6B7280',
                        fontSize: 12
                      }}
                    />

                    <ReTooltip
                      contentStyle={{
                        backgroundColor: "rgba(255,255,255,0.95)",
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                      }}
                      formatter={(v, n) => [`${v}`, n]}
                    />

                    <Line
                      type="monotone"
                      dataKey="Count"
                      name=""                 
                      legendType="none"       
                      stroke={STATUS_COLORS['On time']}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col>
              <Card className="p-3 shadow-sm">
                <AdvanceTableProvider
                  {...table}
                >
                  <AdvanceTable
                    headerClassName="bg-200 text-nowrap align-middle text-center"
                    rowClassName="text-nowrap align-middle text-center"
                    tableProps={{
                      size: 'sm',
                      striped: true,
                      className: 'fs-10 mb-0 overflow-hidden'
                    }}
                  />
                  <div className="mt-3">
                    <AdvanceTableFooter rowInfo
                      navButtons
                      rowsPerPageSelection />
                  </div>
                </AdvanceTableProvider>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Card>
  );
};

export default MedpassDashboard;

